/**
 * Pure label rules for the Activity card's hover details panel.
 *
 * The panel is a stack of `icon + one line of text` rows, so every decision in
 * it is a mapping from thread state to a string. Those mappings live here and
 * the component stays layout-only — the same split `activityThreadCard.logic`
 * already uses for the card itself.
 */

import type { SavedEnvironmentRuntimeIndex } from "./sidebarThreadRuntimeIdentity";
import { resolveThreadOrigin } from "./threadOrigin";
import {
  DEFAULT_APEX_PROFILES,
  getProviderCatalogModel,
  type ModelSelection,
  type ProviderKind,
} from "@cmmd-forge/contracts";

/** t3code's wording, verbatim: "1 terminal process" / "N terminal processes". */
export function terminalProcessLabel(count: number): string {
  return `${count} terminal ${count === 1 ? "process" : "processes"} running`;
}

/**
 * The model's display name, or its raw slug.
 *
 * The slug fallback is deliberate and matches t3code: a thread pinned to a
 * model the local catalog has never heard of (a custom endpoint, a slug newer
 * than the build) still has an identity worth showing, and `claude-opus-5`
 * reads better in the panel than an empty row does.
 *
 * Only `codex` and `claudeAgent` have a built-in catalog; Apex and CMMD name
 * their models through endpoint profiles, and the ACP runtimes (Cursor,
 * OpenCode) discover theirs at session start, so those go straight to the slug.
 */
export function resolveThreadModelLabel(
  modelSelection: Pick<ModelSelection, "provider" | "model"> | null | undefined,
): string | null {
  if (!modelSelection) return null;
  const slug = modelSelection.model.trim();
  if (slug.length === 0) return null;
  const provider: ProviderKind = modelSelection.provider;
  if (provider === "codex" || provider === "claudeAgent") {
    return getProviderCatalogModel(provider, slug)?.name ?? slug;
  }
  if (provider === "apex" || provider === "cmmd") {
    const profile = DEFAULT_APEX_PROFILES.find((candidate) => candidate.slug === slug);
    return profile?.displayName ?? slug;
  }
  return slug;
}

/**
 * The "where does this run" row.
 *
 * A CMMD-managed Sprite is not a machine the user named, owns, or can point at.
 * Resolving it through the generic environment-label path produced whatever
 * string the runtime happened to carry — in practice the *project* title, so
 * the panel read `▤ Forge 1.0 — CMMD Builds CMMD` directly under
 * `▣ forge-1-0-cmmd-builds-cmmd`: the same fact twice, in a panel whose entire
 * job is to say things the card had to truncate away. The answer the row is
 * actually asked is "is this on my machine or is it in the cloud", and for a
 * Sprite that answer is one word.
 *
 * `Cloud` and the cloud glyph are not invented here — the Projects layout
 * already collapses these environments to exactly that string
 * (`projectGroupedSidebar.logic.ts`), and `environmentKind.ts` already maps
 * `forge-cloud` to `CloudIcon`. This makes the Activity panel agree with them
 * rather than adding a third vocabulary.
 *
 * Cloud-ness is read from the environment id, which is the same fallback the
 * Projects layout uses when it has no worktree member to ask. That is why a
 * cloud row survives a null `environmentLabel`: being in the cloud is knowable
 * from the id alone, so the row renders where it used to vanish.
 *
 * The cloud test goes through `resolveThreadOrigin`, the canonical rule shared
 * with the row, so the two cannot label the same thread differently. That
 * resolver reads `classifyEnvironmentId(id).isCloud` rather than
 * `.legacyRuntimeId !== null`, because `isCloud` also matches
 * `cmmd-cloud-project:` — a cloud project registered but not yet backed by a
 * live Sprite. That thread is no less in the cloud for having no VM yet, and it
 * is precisely the case with no sensible machine name to fall back to.
 */
export interface ActivityCardEnvironmentRow {
  readonly isCloud: boolean;
  readonly label: string;
}

export function resolveActivityCardEnvironmentRow(input: {
  readonly environmentId: unknown;
  readonly environmentLabel: string | null;
  readonly savedEnvironments?: SavedEnvironmentRuntimeIndex;
}): ActivityCardEnvironmentRow | null {
  // Routed through the canonical resolver rather than testing the id here, so
  // the row and this tooltip cannot disagree about the same thread. Cloud is
  // decided by the environment identity alone, which is why a null current
  // environment is the honest argument: this surface has no view of it.
  if (
    resolveThreadOrigin({
      threadEnvironmentId: input.environmentId,
      currentEnvironmentId: null,
      ...(input.savedEnvironments ? { savedEnvironments: input.savedEnvironments } : {}),
    }) === "cloud"
  ) {
    return { isCloud: true, label: "Cloud" };
  }
  const label = input.environmentLabel?.trim();
  // A local environment with no resolved label has nothing to say, and an empty
  // row with a lone server glyph reads as a rendering bug rather than as
  // missing data.
  if (label === undefined || label === "") return null;
  return { isCloud: false, label };
}
